import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { SpinnerGap, Warning, CheckCircle, ArrowClockwise, CaretDown, CaretRight, Copy } from '@phosphor-icons/react';

/**
 * Tab "Webhooks Rejeitados" — auditoria de submissões de formulários do
 * website que CHEGARAM ao CRM mas foram rejeitadas (token inválido, campos
 * em falta, form não mapeado, excepções).
 *
 * Permite ao admin ver a submissão completa e integrá-la manualmente,
 * marcando depois como resolvida.
 */
const MOTIVO_LABEL = {
  auth: 'Token inválido',
  validation: 'Campos em falta',
  unmapped_form: 'Form não mapeado',
  processing: 'Erro de processamento',
  exception: 'Excepção',
};
const MOTIVO_COLOR = {
  auth: 'bg-red-100 text-red-700',
  validation: 'bg-amber-100 text-amber-800',
  unmapped_form: 'bg-purple-100 text-purple-700',
  processing: 'bg-orange-100 text-orange-700',
  exception: 'bg-red-100 text-red-700',
};

export default function TabWebhookRejeicoes() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [pendentes, setPendentes] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [showResolved, setShowResolved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/backoffice/webhooks/rejections');
      setRows(r?.data?.rejections || []);
      setPendentes(r?.data?.pendentes || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro a carregar rejeições.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    try {
      await api.post(`/backoffice/webhooks/rejections/${id}/resolve`, {});
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao marcar como resolvida.');
    }
  };

  const parsePayload = (json) => {
    try { return JSON.parse(json || '{}'); } catch { return {}; }
  };

  const copyPayload = (json) => {
    try {
      const obj = parsePayload(json);
      const text = Object.entries(obj).map(([k, v]) => `${k}: ${v}`).join('\n');
      navigator.clipboard?.writeText(text);
    } catch { /* noop */ }
  };

  const visible = rows.filter((r) => showResolved || !r.Resolvido);

  return (
    <div className="space-y-6" data-testid="tab-webhook-rejeicoes">
      <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Warning size={24} weight="fill" className="text-amber-500" />
            <h3 className="text-base font-semibold text-[#111827]">Webhooks Rejeitados</h3>
            {pendentes > 0 && (
              <span className="text-[11px] font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded-full" data-testid="rej-pendentes-badge">
                {pendentes} por resolver
              </span>
            )}
          </div>
          <button type="button" onClick={load} disabled={loading}
            data-testid="rej-refresh-btn"
            className="px-3 py-1.5 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#F3F4F6] inline-flex items-center gap-2 disabled:opacity-50">
            {loading ? <SpinnerGap size={16} className="animate-spin" /> : <ArrowClockwise size={16} />}
            Atualizar
          </button>
        </div>
        <p className="text-xs text-[#6B7280] mb-4">
          Submissões de formulários do website que chegaram ao CRM mas <strong>não foram integradas</strong>.
          Abre cada linha para ver o formulário completo e integrar manualmente na lead.
          <br />
          <span className="text-[11px] text-[#9CA3AF]">
            Nota: submissões que nunca chegam ao servidor (o plugin do site não dispara o webhook) não aparecem aqui — só o email de notificação as revela.
          </span>
        </p>

        <label className="inline-flex items-center gap-2 text-xs text-[#374151] mb-4">
          <input type="checkbox" checked={showResolved} data-testid="rej-show-resolved"
            onChange={(e) => setShowResolved(e.target.checked)} />
          Mostrar também as já resolvidas
        </label>

        {error && (
          <div className="p-3 rounded-md bg-red-50 border border-red-200 text-[#DC2626] text-sm flex items-start gap-2 mb-4">
            <Warning size={18} className="shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        {visible.length === 0 && !loading && (
          <div className="p-6 text-center text-sm text-[#6B7280] bg-[#F9FAFB] rounded-md" data-testid="rej-empty">
            <CheckCircle size={28} weight="fill" className="text-emerald-500 mx-auto mb-2" />
            Sem webhooks rejeitados {showResolved ? '' : 'por resolver'}.
          </div>
        )}

        <div className="space-y-2">
          {visible.map((r) => {
            const isOpen = expanded === r.ID_WebhookRejeitado;
            const payload = parsePayload(r.PayloadJson);
            return (
              <div key={r.ID_WebhookRejeitado} className="border border-[#E5E7EB] rounded-lg overflow-hidden"
                data-testid={`rej-row-${r.ID_WebhookRejeitado}`}>
                <button type="button"
                  onClick={() => setExpanded(isOpen ? null : r.ID_WebhookRejeitado)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#F9FAFB]">
                  {isOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${MOTIVO_COLOR[r.Motivo] || 'bg-gray-100 text-gray-700'}`}>
                    {MOTIVO_LABEL[r.Motivo] || r.Motivo}
                  </span>
                  <span className="text-sm font-medium text-[#111827] truncate">{r.Nome || r.Email || '(sem nome)'}</span>
                  {r.Email && <span className="text-xs font-mono text-[#6B7280] truncate hidden sm:inline">{r.Email}</span>}
                  <span className="ml-auto text-[11px] text-[#9CA3AF] whitespace-nowrap">{r.DataRegisto}</span>
                  <span className="text-[11px] text-[#6B7280] font-mono whitespace-nowrap">{r.Endpoint} · {r.HttpStatus}</span>
                  {r.Resolvido ? (
                    <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">resolvida</span>
                  ) : null}
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 pt-1 bg-[#F9FAFB] border-t border-[#E5E7EB] text-xs">
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mb-3 mt-2">
                      <Detail label="Motivo" value={`${MOTIVO_LABEL[r.Motivo] || r.Motivo} — ${r.Detalhe || ''}`} />
                      <Detail label="Fonte / Endpoint" value={`${r.Source} · ${r.Endpoint} (HTTP ${r.HttpStatus})`} />
                      <Detail label="Email" value={r.Email} mono />
                      <Detail label="Telefone" value={r.Telefone} mono />
                      <Detail label="Form ID" value={r.FormId} mono />
                      <Detail label="Remote IP" value={r.RemoteIp} mono />
                    </div>

                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">Formulário completo</p>
                      <button type="button" onClick={() => copyPayload(r.PayloadJson)}
                        className="text-[11px] text-[#0F1829] hover:underline inline-flex items-center gap-1"
                        data-testid={`rej-copy-${r.ID_WebhookRejeitado}`}>
                        <Copy size={13} /> Copiar campos
                      </button>
                    </div>
                    <div className="bg-white border border-[#E5E7EB] rounded-md p-3 max-h-64 overflow-auto">
                      {Object.keys(payload).length === 0 ? (
                        <span className="text-[#9CA3AF]">(sem payload registado)</span>
                      ) : (
                        <table className="w-full">
                          <tbody>
                            {Object.entries(payload).map(([k, v]) => (
                              <tr key={k} className="border-b border-[#F3F4F6] last:border-0">
                                <td className="py-1 pr-3 text-[#6B7280] align-top whitespace-nowrap font-medium">{k}</td>
                                <td className="py-1 text-[#111827] break-words">{String(v)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {!r.Resolvido && (
                      <div className="mt-3">
                        <button type="button" onClick={() => resolve(r.ID_WebhookRejeitado)}
                          data-testid={`rej-resolve-${r.ID_WebhookRejeitado}`}
                          className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-md hover:bg-emerald-700 inline-flex items-center gap-2">
                          <CheckCircle size={14} weight="bold" /> Marcar como resolvida
                        </button>
                      </div>
                    )}
                    {r.Resolvido && r.ID_LeadResolvida && (
                      <p className="mt-2 text-[11px] text-emerald-700">Integrada na lead #{r.ID_LeadResolvida}.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, mono }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-[#6B7280] whitespace-nowrap">{label}:</span>
      <span className={`text-[#111827] break-all ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}
